import Box from '@mui/material/Box';
import { createSelector } from '@reduxjs/toolkit';
import InputNumber from 'common/components/Elements/Form/InputNumber/InputNumber';
import InputRadioGroup from 'common/components/Elements/Form/InputRadioGroup/InputRadioGroup';
import InputSelect from 'common/components/Elements/Form/InputSelect/InputSelect';
import ModalActions from 'common/components/Elements/Modal/ModalActions/ModalActions';
import SimpleModal from 'common/components/Elements/Modal/SimpleModal/SimpleModal';
import { stringIsNumber } from 'common/utilities/core/string';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import type { AppDispatch } from 'core/redux/store/configureStore.types';
import { unmatchedConstraintsErrorMsg } from 'core/utilities/constraint/constraint';
import type { ValueLabelOption } from 'core/utilities/core.types';
import I18n from 'i18n/en-US';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AlignmentOptions, Session as SequenceAnalyserSession } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import { AlignmentOptionsMethodKeyConstants } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import { runAlignment } from 'sequenceAnalyser/redux/actions/sequenceAnalyserActions';

const methodOptions: ValueLabelOption[] = [
  { label: 'Wilbur and Lipman (fast)', value: AlignmentOptionsMethodKeyConstants.fast },
  { label: 'Dynamic Programming Algorithm (slow)', value: AlignmentOptionsMethodKeyConstants.slow },
];

interface ComponentProps {
  open: boolean;
  onOpen: (open: boolean) => void;
  alignmentToolLabel: SequenceAnalyserSession['alignmentToolLabel'];
}

const ranAlignmentsSelector = createSelector(
  [(state: ApplicationState) => state.sequenceAnalyser],
  (sequenceAnalyser) => [...sequenceAnalyser.ranAlignments].reverse(),
);

function AlignActionModal({
  open, onOpen, alignmentToolLabel,
}: ComponentProps) {
  const dispatch = useDispatch<AppDispatch>();
  const ranAlignments = useSelector(ranAlignmentsSelector);
  const slowRanAlignments = ranAlignments.filter((a) => a.methodKey === AlignmentOptionsMethodKeyConstants.slow);
  const [processing, setProcessing] = useState(false);
  const lastRanAlignment = ranAlignments[0] as AlignmentOptions | undefined;
  const lastSlowRanAlignment = slowRanAlignments[0] as AlignmentOptions | undefined;
  const [methodKey, setMethodKey] = useState(lastRanAlignment?.methodKey || AlignmentOptionsMethodKeyConstants.fast);
  const [slowOptions, setSlowOptions] = useState({
    identity: (lastSlowRanAlignment?.slowOptions?.identity || 40).toString(),
    gapCreationPenalty: (lastSlowRanAlignment?.slowOptions?.gapCreationPenalty || 10).toString(),
    gapExtensionPenalty: (lastSlowRanAlignment?.slowOptions?.gapExtensionPenalty || 0.05).toString(),
  });
  const slowRanAlignmentsOptions = slowRanAlignments.map((a, i) => ({
    label: [
      a.slowOptions?.identity || '',
      a.slowOptions?.gapCreationPenalty || '',
      a.slowOptions?.gapExtensionPenalty || '',
    ].join(' / '),
    value: i.toString(),
  }));
  const [slowRanAlignment, setSlowRanAlignment] = useState<string | undefined>();

  const slowOptionsErrorMsg = {
    identity: unmatchedConstraintsErrorMsg(
      {
        mandatory: false,
        range: { min: 0, max: 100 },
        precision: { integerDigits: 3, fractionalDigits: 0 },
      }, [], slowOptions.identity,
    ),
    gapCreationPenalty: unmatchedConstraintsErrorMsg(
      {
        mandatory: false,
        range: { min: 0, max: 1000 },
        precision: { integerDigits: 4, fractionalDigits: 2 },
      }, [], slowOptions.gapCreationPenalty,
    ),
    gapExtensionPenalty: unmatchedConstraintsErrorMsg(
      {
        mandatory: false,
        range: { min: 0, max: 1000 },
        precision: { integerDigits: 4, fractionalDigits: 2 },
      }, [], slowOptions.gapExtensionPenalty,
    ),
  };

  const buildAlignmentOptions = (): AlignmentOptions => ({
    methodKey,
    slowOptions: methodKey === AlignmentOptionsMethodKeyConstants.slow ? {
      identity: Number(slowOptions.identity),
      gapCreationPenalty: Number(slowOptions.gapCreationPenalty),
      gapExtensionPenalty: Number(slowOptions.gapExtensionPenalty),
    } : {},
  });

  return (
    <SimpleModal
      title={I18n.SequenceAnalyser.TableActions.AlignActionModal.title}
      open={open}
      onClose={() => {
        onOpen(false);
      }}
      content={(
        <>
          <Box mt={2}>
            <InputRadioGroup
              label={I18n.SequenceAnalyser.TableActions.AlignActionModal.description(alignmentToolLabel)}
              value={methodKey}
              options={methodOptions}
              onChange={(value) => {
                setSlowRanAlignment(undefined);
                setMethodKey(value as AlignmentOptionsMethodKeyConstants);
              }}
              modalLabel
            />
            {methodKey === AlignmentOptionsMethodKeyConstants.slow && (
              <>
                {slowRanAlignments.length > 0 && (
                  <InputSelect
                    label={I18n.SequenceAnalyser.TableActions.AlignActionModal.previouslyApplied}
                    options={slowRanAlignmentsOptions}
                    value={slowRanAlignment}
                    onChange={(value) => {
                      const option = slowRanAlignments[Number(value)];

                      if (option.slowOptions) {
                        setSlowRanAlignment(value);
                        setSlowOptions({
                          identity: (option.slowOptions.identity || '').toString(),
                          gapCreationPenalty: (option.slowOptions.gapCreationPenalty || '').toString(),
                          gapExtensionPenalty: (option.slowOptions.gapExtensionPenalty || '').toString(),
                        });
                      }
                    }}
                  />
                )}
                <InputNumber
                  value={slowOptions.identity}
                  label={I18n.SequenceAnalyser.TableActions.AlignActionModal.identityLabel}
                  onChange={(value) => {
                    setSlowOptions((prevState) => ({ ...prevState, identity: value }));
                    setSlowRanAlignment(undefined);
                  }}
                  errorMsg={slowOptionsErrorMsg.identity}
                  modalLabel
                />
                <InputNumber
                  value={slowOptions.gapCreationPenalty}
                  label={I18n.SequenceAnalyser.TableActions.AlignActionModal.gapCreationPenaltyLabel}
                  onChange={(value) => {
                    setSlowRanAlignment(undefined);
                    setSlowOptions((prevState) => ({ ...prevState, gapCreationPenalty: value }));
                  }}
                  errorMsg={slowOptionsErrorMsg.gapCreationPenalty}
                  modalLabel
                />
                <InputNumber
                  value={slowOptions.gapExtensionPenalty}
                  label={I18n.SequenceAnalyser.TableActions.AlignActionModal.gapExtensionPenaltyLabel}
                  onChange={(value) => {
                    setSlowRanAlignment(undefined);
                    setSlowOptions((prevState) => ({ ...prevState, gapExtensionPenalty: value }));
                  }}
                  errorMsg={slowOptionsErrorMsg.gapExtensionPenalty}
                  modalLabel
                />
              </>
            )}
          </Box>
          <ModalActions
            applyText={I18n.SequenceAnalyser.TableActions.AlignActionModal.apply}
            disableApply={
              methodKey === AlignmentOptionsMethodKeyConstants.slow && Object.keys(slowOptions).some((o) => (
                !stringIsNumber(slowOptions[o as keyof typeof slowOptions])
                || slowOptionsErrorMsg[o as keyof typeof slowOptionsErrorMsg].length > 0
              ))
            }
            persisting={processing}
            onApply={() => {
              setProcessing(true);
              dispatch(runAlignment(buildAlignmentOptions()));
              onOpen(false);
            }}
          />
        </>
      )}
    />
  );
}

export default AlignActionModal;
